import { buildDepreciation } from './depreciation.js';
import { ENGINE_MMI_2026, ENGINE_MMI_2026_CORRECTED } from './engine.js';
import { coverageRatio, grossYield, internalRateOfReturn, netYield, paybackYears, } from './kpi.js';
import { buildLoanSchedule } from './loan.js';
import { MONTHS_PER_YEAR, TIMELINE_YEARS, at, bit, zeros } from './series.js';
import { capitalGainsSurcharge, capitalGainsTaxRate, DEFAULT_CAPITAL_GAINS_INCOME_TAX_RATE, } from './tax/fr/capital-gains.js';
/**
 * Le classeur n'a que 36 colonnes d'exploitation : une revente au-delà n'y figurerait
 * jamais, et le cash-flow serait alors silencieusement sans produit de vente.
 */
function assertProjectable(property) {
    const { holdingYears } = property;
    if (!Number.isInteger(holdingYears) || holdingYears < 1 || holdingYears >= TIMELINE_YEARS) {
        throw new RangeError(`holdingYears doit être un entier entre 1 et ${TIMELINE_YEARS - 1} (reçu : ${holdingYears})`);
    }
}
export function projectMmi(assumptions, property, options = {}) {
    assertProjectable(property);
    const engine = options.engine ?? ENGINE_MMI_2026;
    const corrected = engine === ENGINE_MMI_2026_CORRECTED;
    const a = assumptions;
    const p = property;
    const c = p.monthlyCharges;
    const inflate = 1 + a.inflation;
    // ---------------------------------------------------------------- calendrier et drapeaux
    const saleYear = a.acquisitionYear + p.holdingYears; // F20 = F19 + 25
    const depreciationEndYear = a.buildingDepreciationYears + a.acquisitionYear; // F22
    const loanEndYear = a.acquisitionYear + p.loanYears; // F59
    const years = zeros();
    const flags = {
        sale: new Array(TIMELINE_YEARS + 1).fill(false),
        inPortfolio: new Array(TIMELINE_YEARS + 1).fill(false),
        depreciating: new Array(TIMELINE_YEARS + 1).fill(false),
        loanActive: new Array(TIMELINE_YEARS + 1).fill(false),
    };
    years[0] = a.acquisitionYear;
    for (let n = 1; n <= TIMELINE_YEARS; n++) {
        const year = a.acquisitionYear + n - 1; // H6 = Hypothèses!F8, I6 = H6 + 1, ...
        years[n] = year;
        flags.sale[n] = year === saleYear;
        flags.inPortfolio[n] = year <= saleYear;
        flags.depreciating[n] = year < depreciationEndYear;
        flags.loanActive[n] = year < loanEndYear;
    }
    // ---------------------------------------------------------------- investissement initial
    // F33 = SUM(F26:F32)
    const totalProjectCost = p.purchasePrice +
        p.notaryFees +
        p.works +
        p.plan +
        p.furniture +
        p.agencyFees +
        p.guaranteeFees;
    // F21 = (F26 + F28) * (1 + croissance) ^ (F20 - F19)
    const salePrice = (p.purchasePrice + p.works) * (1 + a.housingPriceGrowth) ** p.holdingYears;
    // F51 = H51 * F33 avec H51 = 1 - H52 et H52 = F52 / F33. Le bruit de flottant est voulu.
    const equity = (1 - p.debt / totalProjectCost) * totalProjectCost;
    // ---------------------------------------------------------------- prêt et amortissement
    const loan = buildLoanSchedule({
        principal: p.debt,
        annualRate: p.loanAnnualRate,
        years: p.loanYears,
        mode: options.loanMode ?? (corrected ? 'monthly-exact' : 'annual-excel'),
    });
    const depreciation = buildDepreciation({
        // G139 = F26 + F28 ; F3 : le terrain n'est pas amortissable (il ne porte que sur le prix).
        buildingBase: corrected
            ? p.purchasePrice * (1 - a.landShare) + p.works
            : p.purchasePrice + p.works,
        furnitureBase: p.furniture, // G144 = F30
        buildingYears: a.buildingDepreciationYears,
        furnitureYears: a.furnitureDepreciationYears,
        totalProjectCost,
        depreciating: flags.depreciating,
        inPortfolio: flags.inPortfolio,
    });
    // ---------------------------------------------------------------- compte de résultat
    const is = {
        revenue: zeros(),
        managementFees: zeros(),
        notaryFees: zeros(),
        agencyFees: zeros(),
        condo: zeros(),
        propertyTax: zeros(),
        cfe: zeros(),
        pno: zeros(),
        accounting: zeros(),
        energy: zeros(),
        internet: zeros(),
        ebitda: zeros(),
        depreciation: zeros(),
        operatingIncome: zeros(),
        financialResult: zeros(),
        incomeBeforeTax: zeros(),
        taxation: zeros(),
        netIncome: zeros(),
    };
    // ---------------------------------------------------------------- calculateur d'impôt
    const tax = {
        incomeBeforeTax: zeros(),
        theoreticalTax: zeros(),
        lossesBop: zeros(),
        lossesCreated: zeros(),
        lossesUsed: zeros(),
        lossesEop: zeros(),
        incomeTax: zeros(),
    };
    const taxRate = a.personalTaxRate + a.socialContributionRate; // F116 = F167
    for (let n = 1; n <= TIMELINE_YEARS; n++) {
        const first = n === 1;
        const held = bit(flags.inPortfolio[n]);
        // H92 = F37 * Douze_Mois * H124 ; puis I92 = H92 * (1 + inflation) * I124.
        // Pas de vacance ici (F5) — sauf dans mmi-2026-corrected.
        const rentedMonths = corrected ? MONTHS_PER_YEAR - p.vacancyMonths : MONTHS_PER_YEAR;
        is.revenue[n] = first
            ? p.monthlyRent * rentedMonths * held
            : at(is.revenue, n - 1) * inflate * held;
        // Charges figées : jamais indexées dans le compte de résultat (F4) — sauf dans
        // mmi-2026-corrected, où elles suivent la même chaîne indexée que le cash-flow.
        const charge = (series, monthly) => corrected && !first
            ? at(series, n - 1) * inflate * held
            : -monthly * MONTHS_PER_YEAR * held;
        is.managementFees[n] = -at(is.revenue, n) * p.managementFeeRate; // H95 = -H92 * F46
        is.notaryFees[n] = first ? -p.notaryFees : 0; // H96 = -F27, puis 0
        is.agencyFees[n] = first ? -p.agencyFees : 0; // H97 = -F31, puis 0
        is.condo[n] = charge(is.condo, c.condo);
        is.propertyTax[n] = charge(is.propertyTax, c.propertyTax);
        is.cfe[n] = first ? 0 : -c.cfe * MONTHS_PER_YEAR * held; // H100 est vide, jamais indexée
        is.pno[n] = charge(is.pno, c.pno);
        is.accounting[n] = charge(is.accounting, c.accounting);
        is.energy[n] = charge(is.energy, c.energy);
        is.internet[n] = charge(is.internet, c.internet);
        // H105 = H92 + SUM(H95:H104)
        const charges = at(is.managementFees, n) +
            at(is.notaryFees, n) +
            at(is.agencyFees, n) +
            at(is.condo, n) +
            at(is.propertyTax, n) +
            at(is.cfe, n) +
            at(is.pno, n) +
            at(is.accounting, n) +
            at(is.energy, n) +
            at(is.internet, n);
        is.ebitda[n] = at(is.revenue, n) + charges;
        is.depreciation[n] = at(depreciation.bookValue.depreciation, n) * held; // H108
        is.operatingIncome[n] = at(is.ebitda, n) + at(is.depreciation, n);
        is.financialResult[n] = -at(loan.interest, n) * held; // H112 = -H160 * H124
        is.incomeBeforeTax[n] = at(is.operatingIncome, n) + at(is.financialResult, n);
        // -- Calculateur d'impôt (l. 165-173) : le report de pertes se fait en euros d'impôt.
        tax.incomeBeforeTax[n] = at(is.incomeBeforeTax, n);
        tax.theoreticalTax[n] = taxRate * at(tax.incomeBeforeTax, n);
        const theoretical = at(tax.theoreticalTax, n);
        tax.lossesBop[n] = at(tax.lossesEop, n - 1);
        tax.lossesCreated[n] = Math.max(-theoretical, 0); // = -MIN(H167 ; 0)
        // H171. Quand l'impôt théorique est exactement nul, le classeur « utilise » tout
        // le stock de pertes : voir le test de caractérisation dans projection.golden.test.ts.
        const previousLosses = at(tax.lossesEop, n - 1);
        if (theoretical < 0) {
            tax.lossesUsed[n] = 0;
        }
        else if (theoretical > 0 && previousLosses > theoretical) {
            tax.lossesUsed[n] = -theoretical;
        }
        else {
            // F9 : un impôt théorique exactement nul n'impute aucune perte (`mmi-2026-corrected`).
            tax.lossesUsed[n] = corrected && theoretical === 0 ? 0 : -previousLosses;
        }
        tax.lossesEop[n] =
            at(tax.lossesBop, n) + at(tax.lossesCreated, n) + at(tax.lossesUsed, n);
        tax.incomeTax[n] = theoretical < 0 ? 0 : theoretical + at(tax.lossesUsed, n);
        is.taxation[n] = -at(tax.incomeTax, n); // H116 = -H173
        is.netIncome[n] = at(is.incomeBeforeTax, n) + at(is.taxation, n);
    }
    // ---------------------------------------------------------------- cash-flow
    const cf = {
        rent: zeros(),
        managementFees: zeros(),
        condo: zeros(),
        propertyTax: zeros(),
        cfe: zeros(),
        pno: zeros(),
        accounting: zeros(),
        energy: zeros(),
        internet: zeros(),
        interest: zeros(),
        debtFlow: zeros(),
        insurance: zeros(),
        incomeTax: zeros(),
        investment: zeros(),
        salePrice: zeros(),
        capitalGainsTax: zeros(),
        total: zeros(),
        monthly: zeros(),
        monthlyEnrichment: zeros(),
        annualEnrichment: zeros(),
    };
    // t = 0 : G77 = F52 (le prêt est reçu), G80 = -F33 (le projet est payé).
    cf.debtFlow[0] = p.debt;
    cf.investment[0] = -totalProjectCost;
    cf.total[0] = cf.debtFlow[0] + cf.investment[0];
    for (let n = 1; n <= TIMELINE_YEARS; n++) {
        const first = n === 1;
        const held = bit(flags.inPortfolio[n]);
        const sold = bit(flags.sale[n]);
        // H65 = F37 * (Douze_Mois - vacance) ; puis chaîne indexée : I65 = H65 * (1 + i) * I124.
        const indexed = (previous, firstYear) => first ? firstYear : at(previous, n - 1) * inflate * held;
        cf.rent[n] = indexed(cf.rent, p.monthlyRent * (MONTHS_PER_YEAR - p.vacancyMonths));
        cf.managementFees[n] = indexed(cf.managementFees, -at(cf.rent, 1) * p.managementFeeRate);
        cf.condo[n] = indexed(cf.condo, -c.condo * MONTHS_PER_YEAR * held);
        cf.propertyTax[n] = indexed(cf.propertyTax, -c.propertyTax * MONTHS_PER_YEAR * held);
        // H70 est vide et I70 ne porte pas d'indexation : la CFE reste nominale.
        cf.cfe[n] = first ? 0 : -c.cfe * MONTHS_PER_YEAR * held;
        cf.pno[n] = indexed(cf.pno, -c.pno * MONTHS_PER_YEAR * held);
        cf.accounting[n] = indexed(cf.accounting, -c.accounting * MONTHS_PER_YEAR * held);
        // H73 et H74 n'ont pas de drapeau en année 1 ; il vaut 1 de toute façon.
        cf.energy[n] = indexed(cf.energy, -c.energy * MONTHS_PER_YEAR);
        cf.internet[n] = indexed(cf.internet, -c.internet * MONTHS_PER_YEAR);
        // H76 = -H160. F13 : le classeur continue de payer le prêt après la revente, et n'en
        // rembourse jamais le capital restant dû ; mmi-2026-corrected s'arrête et solde la dette.
        cf.interest[n] = -at(loan.interest, n) * (corrected ? held : 1);
        // H157 = H161 + H160 : paiement (négatif) + intérêts = capital remboursé (négatif).
        cf.debtFlow[n] = corrected
            ? -(at(loan.principalRepaid, n) * held + at(loan.eop, n) * sold)
            : -at(loan.principalRepaid, n);
        cf.insurance[n] = indexed(cf.insurance, -p.loanInsuranceMonthly * MONTHS_PER_YEAR);
        cf.incomeTax[n] = -at(tax.incomeTax, n); // H79 = -H173
        cf.investment[n] = 0;
        cf.salePrice[n] = salePrice * sold; // H82 = F21 * H123
        // H83 = SI(-taux * (H82 - F26 - F27 - F28) * H123 > 0 ; 0 ; ce montant)
        const gain = at(cf.salePrice, n) - p.purchasePrice - p.notaryFees - p.works;
        const capitalGainsTax = corrected
            ? -correctedCapitalGainsTax(a, p.holdingYears, gain) * sold
            : -a.capitalGainsTaxRate * gain * sold;
        cf.capitalGainsTax[n] = capitalGainsTax > 0 ? 0 : capitalGainsTax;
        // H85 = SUM(H65:H83), dans l'ordre des lignes.
        cf.total[n] =
            at(cf.rent, n) +
                at(cf.managementFees, n) +
                at(cf.condo, n) +
                at(cf.propertyTax, n) +
                at(cf.cfe, n) +
                at(cf.pno, n) +
                at(cf.accounting, n) +
                at(cf.energy, n) +
                at(cf.internet, n) +
                at(cf.interest, n) +
                at(cf.debtFlow, n) +
                at(cf.insurance, n) +
                at(cf.incomeTax, n) +
                at(cf.investment, n) +
                at(cf.salePrice, n) +
                at(cf.capitalGainsTax, n);
        cf.monthly[n] = at(cf.total, n) / MONTHS_PER_YEAR; // H86
        cf.monthlyEnrichment[n] = at(cf.monthly, n) - at(cf.debtFlow, n) / MONTHS_PER_YEAR; // H87
        cf.annualEnrichment[n] = -at(cf.debtFlow, n) + at(cf.total, n); // H88
    }
    // ---------------------------------------------------------------- indicateurs
    // F57 = H112 / 12 ; F58 = F56 - F57 (en valeur absolue ici).
    const monthlyInterest = -at(is.financialResult, 1) / MONTHS_PER_YEAR;
    // F5 = SUM(F37:F45) * (12 - vacance) / F33. F45 = -F46 * F37.
    const monthlyNetIncome = p.monthlyRent -
        c.condo -
        c.propertyTax -
        c.cfe -
        c.pno -
        c.accounting -
        c.energy -
        c.internet -
        p.managementFeeRate * p.monthlyRent;
    const kpi = {
        irr: internalRateOfReturn(cf.total),
        monthlyCashFlow: at(cf.total, 1) / MONTHS_PER_YEAR, // F3 = H85 / 12
        grossYield: grossYield(p.monthlyRent, p.vacancyMonths, totalProjectCost),
        netYield: netYield(monthlyNetIncome, p.vacancyMonths, totalProjectCost),
        paybackYears: paybackYears(equity, at(cf.total, 1)),
        coverageRatio: coverageRatio(p.monthlyRent, loan.monthlyPayment),
    };
    return {
        engine,
        years,
        flags,
        summary: {
            totalProjectCost,
            equity,
            salePrice,
            saleYear,
            depreciationEndYear,
            loanEndYear,
        },
        loan: {
            ...loan,
            monthlyInterest,
            monthlyPrincipal: loan.monthlyPayment - monthlyInterest,
        },
        depreciation,
        incomeStatement: is,
        tax,
        cashFlow: cf,
        kpi,
    };
}
/**
 * P6 : impôt sur la plus-value du foyer avec ses composantes (abattements de durée), plus la
 * surtaxe des plus-values imposables supérieures à 50 000 €. Nul en cas de moins-value.
 */
function correctedCapitalGainsTax(a, yearsHeld, gain) {
    if (!(gain > 0))
        return 0;
    const rate = capitalGainsTaxRate({
        yearsHeld,
        incomeTaxRate: a.capitalGainsIncomeTaxRate ?? DEFAULT_CAPITAL_GAINS_INCOME_TAX_RATE,
        socialRate: a.socialContributionRate,
    });
    return gain * rate.total + capitalGainsSurcharge(gain * (1 - rate.incomeTaxAllowance));
}
//# sourceMappingURL=projection.js.map